import { useEffect, useRef, useState } from "react";

// Кружок (Telegram video note): круглое видео, клик = play/pause, снизу —
// таймер и кнопка транскрибации (recognizeSpeech, как у голосовых).
// Нативный <video controls> в круге выглядел чужеродно, поэтому управление
// своё, по образцу voice-message.tsx.

const formatClock = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export interface VideoNoteMessageProps {
  url?: string | undefined;
  thumbnailUrl?: string | undefined;
  mimeType?: string | undefined;
  durationSeconds?: number | undefined;
  transcript?: string | undefined;
  transcriptStatus?: "pending" | "done" | "failed" | undefined;
  onTranscribe?: (() => void) | undefined;
}

export const VideoNoteMessage = ({
  url,
  thumbnailUrl,
  mimeType,
  durationSeconds,
  transcript,
  transcriptStatus,
  onTranscribe
}: VideoNoteMessageProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(true);

  const effectiveDuration =
    mediaDuration && Number.isFinite(mediaDuration) && mediaDuration > 0
      ? mediaDuration
      : durationSeconds && durationSeconds > 0
        ? durationSeconds
        : 0;

  useEffect(() => {
    // Файл докачался и url сменился — сбрасываем плеер.
    setIsPlaying(false);
    setCurrentTime(0);
    setMediaDuration(null);
  }, [url]);

  const togglePlayback = (): void => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      void video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  };

  return (
    <div className="video-note">
      <button
        type="button"
        className="video-note__circle"
        onClick={togglePlayback}
        disabled={!url}
        aria-label={isPlaying ? "Пауза" : "Смотреть видеосообщение"}
        title={isPlaying ? "Пауза" : "Смотреть видеосообщение"}
      >
        {url ? (
          <video
            ref={videoRef}
            className="video-note__video"
            // none: строки виртуализованы и перемонтируются при скролле;
            // metadata дёргал бы gateway Range-запросом на каждый проход.
            preload="none"
            playsInline
            poster={thumbnailUrl ?? undefined}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => {
              setIsPlaying(false);
              setCurrentTime(0);
            }}
            onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
            onLoadedMetadata={(event) => {
              const value = event.currentTarget.duration;
              if (Number.isFinite(value) && value > 0) {
                setMediaDuration(value);
              }
            }}
          >
            <source src={url} type={mimeType ?? "video/mp4"} />
          </video>
        ) : thumbnailUrl ? (
          <img className="video-note__video" src={thumbnailUrl} alt="Видеосообщение" />
        ) : (
          <span className="video-note__placeholder">🎥</span>
        )}
        {!isPlaying ? (
          <span className="video-note__play-overlay" aria-hidden="true">
            <svg viewBox="0 0 16 16">
              <path d="M4.4 2.6c0-.9 1-1.5 1.8-1L13.4 6c.8.5.8 1.6 0 2.1l-7.2 4.4c-.8.5-1.8-.1-1.8-1V2.6z" />
            </svg>
          </span>
        ) : null}
      </button>
      <div className="video-note__meta">
        <span className="video-note__time">
          {isPlaying || currentTime > 0
            ? `${formatClock(currentTime)} / ${formatClock(effectiveDuration)}`
            : effectiveDuration > 0
              ? formatClock(effectiveDuration)
              : "…"}
        </span>
        {!url ? <span className="video-note__loading">Загружаем…</span> : null}
        {onTranscribe && transcriptStatus !== "done" ? (
          <button
            type="button"
            className="voice-msg__transcribe"
            onClick={onTranscribe}
            disabled={transcriptStatus === "pending"}
            title="Распознать видеосообщение в текст (нужен Telegram Premium на аккаунте)"
            aria-label="Распознать видеосообщение в текст"
          >
            {transcriptStatus === "pending" ? "…" : "→A"}
          </button>
        ) : null}
        {transcriptStatus === "done" && transcript ? (
          <button
            type="button"
            className="voice-msg__transcribe voice-msg__transcribe--active"
            onClick={() => setTranscriptOpen((value) => !value)}
            title={transcriptOpen ? "Скрыть текст" : "Показать текст"}
            aria-label={transcriptOpen ? "Скрыть текст" : "Показать текст"}
          >
            →A
          </button>
        ) : null}
      </div>
      {transcriptStatus === "pending" ? (
        <div className="voice-msg__transcript voice-msg__transcript--pending">Распознаём…</div>
      ) : null}
      {transcriptStatus === "failed" ? (
        <div className="voice-msg__transcript voice-msg__transcript--failed">
          Не удалось распознать речь.
        </div>
      ) : null}
      {transcriptStatus === "done" && transcript && transcriptOpen ? (
        <div className="voice-msg__transcript">{transcript}</div>
      ) : null}
    </div>
  );
};
